import { useState } from 'react';
import Loading from "../../../components/UI/Loading/Loading";
import { Workflow, Upload } from 'lucide-react';
import '../../../components/ManageBotsModal/ManageBotsModal.css';
import { useFlowList, MAX_FLOWS } from './useFlowList';
import FolderChips from './components/flowlist/FolderChips';
import FlowCard from './components/flowlist/FlowCard';
import FoldersModal from './components/flowlist/FoldersModal';
import MoveFlowModal from './components/flowlist/MoveFlowModal';
import CreateFlowModal from './components/flowlist/CreateFlowModal';
import ExportFlowModal from './components/flowlist/ExportFlowModal';
import ImportFlowModal from './components/flowlist/ImportFlowModal';

export default function FlowList() {
  const {
    loading, bots, photoUrls, flows, folders,
    folderFilter, setFolderFilter, folderCounts, noFolderCount, visibleFlows,
    botLabelFor, defaultBot,
    createFolder, saveFolder, deleteFolder, moveToFolder,
    toggleFlow, deleteFlow, editFlow, createFlow, fetchExportCode, importFlow,
  } = useFlowList();

  // Somente o controle de qual modal está aberto vive aqui.
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [moveFlow, setMoveFlow] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [exportFlow, setExportFlow] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  const atLimit = flows.length >= MAX_FLOWS;

  async function handleMove(flow, folderId) {
    const ok = await moveToFolder(flow, folderId);
    if (ok) setMoveFlow(null);
  }

  return (
    <div className="page">
      <header className='mb-6 flex space-between flex flex-wrap flex-row'>
        <div>
          <h1 className="title">Fluxos</h1>
          <p className='text-gray-400'>Gerencie os fluxos de conversa dos seus bots</p>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <button className="btn-secondary flex items-center gap-1.5 text-sm" onClick={() => setShowImportModal(true)} disabled={!bots.length}>
            <Upload size={14} /> Importar
          </button>
          <button className="btn-primary" onClick={() => setShowCreateModal(true)} disabled={!bots.length || atLimit}>
            <i className="fas fa-plus" /> Novo Fluxo
            <span className={`mt-[2px] ml-2 text-[10px] ${atLimit ? 'text-red-400' : 'text-white/40'}`}>
              ({flows.length}/{MAX_FLOWS})
            </span>
          </button>
        </div>
      </header>

      {loading ? (
        <Loading text="Carregando Fluxos..." />
      ) : (
        <div className="mt-2">
          <FolderChips
            folders={folders}
            flowsCount={flows.length}
            folderCounts={folderCounts}
            noFolderCount={noFolderCount}
            folderFilter={folderFilter}
            onFilterChange={setFolderFilter}
            onManageFolders={() => setShowFolderModal(true)}
          />

          {visibleFlows.length === 0 && (
            <div className="empty-state">
              <Workflow size={40} className="opacity-40" />
              <h4>{folderFilter === 'all' ? 'Nenhum fluxo criado' : 'Nenhum fluxo aqui'}</h4>
              <p>
                {folderFilter === 'all'
                  ? 'Crie um fluxo para automatizar a conversa dos seus bots.'
                  : 'Nenhum fluxo nesta pasta. Mova fluxos usando o botão de pasta no card.'}
              </p>
            </div>
          )}

          {/* Grid de cards — 3 por linha no desktop, responsivo */}
          {visibleFlows.length > 0 && (
            <div className="sx-card-grid">
              {visibleFlows.map(flow => (
                <FlowCard
                  key={flow.id}
                  flow={flow}
                  folder={folders.find(f => f.id === flow.folder_id)}
                  botLabel={botLabelFor(flow)}
                  botPhotoUrl={photoUrls[flow.bot_id]}
                  onToggle={toggleFlow}
                  onMove={setMoveFlow}
                  onExport={setExportFlow}
                  onDelete={deleteFlow}
                  onEdit={editFlow}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {showFolderModal && (
        <FoldersModal
          folders={folders}
          folderCounts={folderCounts}
          onClose={() => setShowFolderModal(false)}
          onCreate={createFolder}
          onSave={saveFolder}
          onDelete={deleteFolder}
        />
      )}

      {moveFlow && (
        <MoveFlowModal
          flow={moveFlow}
          folders={folders}
          onClose={() => setMoveFlow(null)}
          onMove={handleMove}
        />
      )}

      {showCreateModal && (
        <CreateFlowModal
          bots={bots}
          initialBot={defaultBot()}
          onClose={() => setShowCreateModal(false)}
          onCreate={createFlow}
        />
      )}

      {exportFlow && (
        <ExportFlowModal
          flow={exportFlow}
          onClose={() => setExportFlow(null)}
          fetchCode={fetchExportCode}
        />
      )}

      {showImportModal && (
        <ImportFlowModal
          bots={bots}
          initialBot={defaultBot()}
          onClose={() => setShowImportModal(false)}
          onImport={importFlow}
        />
      )}
    </div>
  );
}
